import { AnswerHeadline } from "@nimit9/signet-ui/components/answer-headline"
import { Badge } from "@nimit9/signet-ui/components/badge"
import { Button } from "@nimit9/signet-ui/components/button"
import { Card } from "@nimit9/signet-ui/components/card"
import { ErrorState } from "@nimit9/signet-ui/components/error-state"
import { Input } from "@nimit9/signet-ui/components/input"
import { RatioProgress, RatioText } from "@nimit9/signet-ui/components/privacy-mask"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@nimit9/signet-ui/components/select"
import { Skeleton } from "@nimit9/signet-ui/components/skeleton"
import { StatStrip } from "@nimit9/signet-ui/components/stat-strip"
import { useMediaQuery } from "@nimit9/signet-ui/hooks/use-media-query"
import { createFileRoute, Link } from "@tanstack/react-router"
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react"
import { useEffect, useRef, useState } from "react"
import { Amount } from "@/components/common/amount"
import { Header } from "@/components/layout/header"
import { PageWrapper } from "@/components/layout/page-wrapper"
import { type BudgetWithActual, useBudgets } from "@/hooks/use-budgets"
import { useCurrencyFormat } from "@/hooks/use-currency-format"
import { renderAnswerAmount, toSignetHeadline } from "@/lib/answer-headline"
import { cn } from "@/lib/cn"
import { budgetPeriodPhrase, buildBudgetsHeadline } from "./-headline"

export const Route = createFileRoute("/budgets/")({
  component: Budgets,
})

const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
]
const MONTH_ITEMS = Object.fromEntries(MONTHS.map((m, i) => [String(i + 1), m]))

/** Amber indicator for near/over — never red (red is for destructive actions only). */
const AMBER_BAR = "[&_[data-slot=progress-indicator]]:bg-accent-amber"

function BudgetCard({ budget }: { budget: BudgetWithActual }) {
  const fmt = useCurrencyFormat()
  const budgeted = parseFloat(budget.amount)
  const actual = Math.max(0, parseFloat(budget.actual))
  const pct = budgeted > 0 ? (actual / budgeted) * 100 : actual > 0 ? 100 : 0
  const isOver = actual > budgeted
  const isNear = !isOver && pct >= 80
  const remaining = budgeted - actual

  return (
    <Card data-testid="budget-card" size="sm" className="min-w-0 gap-3 px-4">
      <div className="flex items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {budget.category?.icon && <span className="text-callout">{budget.category.icon}</span>}
          <p className="truncate text-body font-medium text-text-primary">
            {budget.category?.name ?? "Uncategorised"}
          </p>
        </div>
        {isOver ? (
          <Badge
            variant="outline"
            className="border-accent-amber/40 bg-accent-amber-subtle text-accent-amber"
          >
            Over
          </Badge>
        ) : isNear ? (
          <Badge variant="outline" className="border-accent-amber/40 text-accent-amber">
            Near limit
          </Badge>
        ) : (
          <Badge variant="secondary">On track</Badge>
        )}
      </div>

      <RatioProgress
        value={Math.min(pct, 100)}
        aria-label={`${budget.category?.name ?? "Uncategorised"} budget used`}
        className={cn("gap-0", (isOver || isNear) && AMBER_BAR)}
      />

      <div className="flex items-center justify-between gap-2 text-caption">
        <span className="text-text-secondary">
          <Amount value={actual} /> spent
        </span>
        <span className="text-text-muted">of {fmt(budgeted)}</span>
      </div>

      <p className={cn("text-caption", isOver ? "text-accent-amber" : "text-text-muted")}>
        {isOver ? `${fmt(Math.abs(remaining))} over budget` : `${fmt(remaining)} remaining`}
      </p>
    </Card>
  )
}

const MIN_YEAR = 2020
const MAX_YEAR = 2100

/** A whole 4-digit year in [MIN_YEAR, MAX_YEAR], else null. */
function parseValidYear(text: string): number | null {
  if (!/^\d{4}$/.test(text.trim())) return null
  const y = Number(text)
  return y >= MIN_YEAR && y <= MAX_YEAR ? y : null
}

function MonthPicker({
  month,
  year,
  onChange,
}: {
  month: number
  year: number
  onChange: (month: number, year: number) => void
}) {
  // The year box keeps its own draft text: clearing it or typing "20" must not
  // query /budgets with year 0 / 20. Only a whole year in range is committed;
  // leaving the box with an invalid draft snaps it back to the current year.
  const [yearDraft, setYearDraft] = useState(String(year))
  useEffect(() => setYearDraft(String(year)), [year])
  const onYearInput = (text: string) => {
    setYearDraft(text)
    const y = parseValidYear(text)
    if (y !== null && y !== year) onChange(month, y)
  }
  const step = (delta: number) => {
    const d = new Date(year, month - 1 + delta)
    onChange(d.getMonth() + 1, d.getFullYear())
  }
  return (
    <div data-testid="month-picker" className="flex min-w-0 items-center gap-1.5">
      <Button
        variant="ghost"
        size="icon-touch"
        className="md:size-8"
        aria-label="Previous month"
        onClick={() => step(-1)}
      >
        <ChevronLeftIcon />
      </Button>
      <Select
        value={String(month)}
        onValueChange={(v) => onChange(Number(v), year)}
        items={MONTH_ITEMS}
      >
        <SelectTrigger size="sm" aria-label="Month" className="min-w-0 flex-1 md:w-32 md:flex-none">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {MONTHS.map((m, i) => (
            <SelectItem key={m} value={String(i + 1)}>
              {m}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Input
        type="number"
        aria-label="Year"
        value={yearDraft}
        min={MIN_YEAR}
        max={MAX_YEAR}
        onChange={(e) => onYearInput(e.target.value)}
        onBlur={() => setYearDraft(String(year))}
        className="h-7 w-20"
      />
      <Button
        variant="ghost"
        size="icon-touch"
        className="md:size-8"
        aria-label="Next month"
        onClick={() => step(1)}
      >
        <ChevronRightIcon />
      </Button>
    </div>
  )
}

function Budgets() {
  const isDesktop = useMediaQuery("(min-width: 768px)")
  const now = new Date()
  const [month, setMonth] = useState(now.getMonth() + 1)
  const [year, setYear] = useState(now.getFullYear())
  const { data, isLoading, isError, error, refetch } = useBudgets(month, year)

  // On first load, if the current month has no budgets, jump to the most recent
  // month that does — so the page doesn't open on an empty month despite having
  // budgets elsewhere (#60). Only auto-redirects once; the user can navigate freely after.
  const didAutoSelect = useRef(false)
  useEffect(() => {
    if (didAutoSelect.current || !data) return
    const latest = data.availableMonths?.[0]
    if (data.data.length === 0 && latest && (latest.month !== month || latest.year !== year)) {
      didAutoSelect.current = true
      setMonth(latest.month)
      setYear(latest.year)
    } else if (data.data.length > 0) {
      didAutoSelect.current = true
    }
  }, [data, month, year])

  const budgets = data?.data ?? []
  const totalBudgeted = budgets.reduce((s, b) => s + parseFloat(b.amount), 0)
  const totalActual = budgets.reduce((s, b) => s + Math.max(0, parseFloat(b.actual)), 0)
  const overCount = budgets.filter((b) => parseFloat(b.actual) > parseFloat(b.amount)).length
  const overallPct = totalBudgeted > 0 ? (totalActual / totalBudgeted) * 100 : 0
  const overallOver = totalActual > totalBudgeted
  const headline = buildBudgetsHeadline({ budgets, period: budgetPeriodPhrase(month, year) })

  const picker = (
    <MonthPicker
      month={month}
      year={year}
      onChange={(m, y) => {
        setMonth(m)
        setYear(y)
      }}
    />
  )
  // Desktop: the picker sits in the header. Phone: the header is too narrow
  // (search + alerts live there), so it leads the page body instead.
  const header = (
    <Header title="Budgets" showPeriod={false} action={isDesktop ? picker : undefined} />
  )
  const phonePicker = !isDesktop && <div className="mb-4">{picker}</div>

  if (isLoading) {
    return (
      <>
        {header}
        <PageWrapper>
          {phonePicker}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3">
            {["a", "b", "c", "d", "e", "f"].map((k) => (
              <Skeleton key={k} className="h-32 animate-pulse rounded-xl" />
            ))}
          </div>
        </PageWrapper>
      </>
    )
  }

  if (isError) {
    return (
      <>
        {header}
        <PageWrapper>
          {phonePicker}
          <ErrorState
            message={error instanceof Error ? error.message : undefined}
            onRetry={refetch}
          />
        </PageWrapper>
      </>
    )
  }

  return (
    <>
      {header}
      <PageWrapper>
        {phonePicker}
        {budgets.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <p className="text-callout font-medium text-text-primary">
              No budgets for {MONTHS[month - 1]} {year}
            </p>
            <p className="mt-1 text-body text-text-secondary">
              Set a monthly limit per category. Paisa warns you before you go over.
            </p>
            <Button
              size="touch"
              className="mt-4"
              render={<Link to="/settings/budgets" />}
              nativeButton={false}
            >
              Set budgets
            </Button>
          </div>
        ) : (
          <div
            data-testid="budgets-page"
            data-layout={isDesktop ? "desktop" : "phone"}
            className="flex min-w-0 flex-col gap-4"
          >
            {headline && (
              <AnswerHeadline
                headline={toSignetHeadline(headline)}
                renderValue={renderAnswerAmount}
              />
            )}

            <StatStrip
              isDesktop={isDesktop}
              items={[
                { label: "Total Budgeted", value: <Amount value={totalBudgeted} /> },
                {
                  label: "Total Spent",
                  value: <Amount value={totalActual} />,
                  meta: `${overallPct.toFixed(0)}% of budget`,
                  tone: overallOver ? "warn" : "muted",
                },
                {
                  label: "Over Budget",
                  value: `${overCount} ${overCount === 1 ? "category" : "categories"}`,
                  meta: overCount > 0 ? "Needs attention" : "All within limits",
                  tone: overCount > 0 ? "warn" : "good",
                },
              ]}
            />

            <Card size="sm" className="gap-2 px-4">
              <div className="flex justify-between gap-2 text-caption">
                <span className="text-text-muted">
                  <Amount value={totalActual} /> of <Amount value={totalBudgeted} />
                </span>
                <span className={overallOver ? "text-accent-amber" : "text-text-muted"}>
                  <RatioText>{`${overallPct.toFixed(0)}%`}</RatioText>
                </span>
              </div>
              <RatioProgress
                value={Math.min(overallPct, 100)}
                aria-label="Total budget used"
                className={cn("gap-0", overallOver && AMBER_BAR)}
              />
            </Card>

            <div
              data-testid="budgets-grid"
              className="grid min-w-0 grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-3"
            >
              {budgets.map((b) => (
                <BudgetCard key={b.id} budget={b} />
              ))}
            </div>
          </div>
        )}
      </PageWrapper>
    </>
  )
}
